import type * as GeoJSON from 'geojson'
import type { LngLatBoundsLike } from 'maplibre-gl'
import type { ContactMarker, TurfBoundary, HouseholdMarker } from '../../types/turfMap'
import type { WorkplaceMarker } from '../../types/workplaceOverlay'
import { HOUSEHOLD_COLORS } from '../../lib/mapConfig'

export function contactsToGeoJson(contacts: ContactMarker[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: contacts.map(contact => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [contact.longitude, contact.latitude],
      },
      properties: {
        id: contact.id,
        label: contact.label,
        walkOrder: contact.walkOrder,
        isVisited: contact.isVisited ?? false,
      },
    })),
  }
}

export function householdsToGeoJson(households: HouseholdMarker[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: households.map(household => {
      const allVisited = household.visitedCount === household.contactCount
      const noneVisited = household.visitedCount === 0
      const hasRevisits = household.needsRevisitCount > 0

      let color: string
      if (allVisited && !hasRevisits) color = HOUSEHOLD_COLORS.all_visited
      else if (noneVisited) color = HOUSEHOLD_COLORS.none_visited
      else color = HOUSEHOLD_COLORS.partial_visited

      return {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [household.longitude, household.latitude],
        },
        properties: {
          householdKey: household.householdKey,
          streetAddress: household.streetAddress,
          city: household.city,
          state: household.state,
          zip: household.zip,
          contactCount: household.contactCount,
          visitedCount: household.visitedCount,
          color,
        },
      }
    }),
  }
}

export function boundariesToGeoJson(boundaries: TurfBoundary[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: boundaries.map(boundary => ({
      ...boundary.geoJson,
      properties: {
        ...boundary.geoJson.properties,
        id: boundary.id,
        label: boundary.label,
        color: boundary.color ?? '#3182ce',
      },
    })),
  }
}

export function computeBounds(
  boundaries?: TurfBoundary[],
  contacts?: ContactMarker[],
  households?: HouseholdMarker[],
  route?: GeoJSON.Feature<GeoJSON.LineString> | null,
  workplaces?: WorkplaceMarker[],
): LngLatBoundsLike | null {
  const coords: [number, number][] = []

  if (boundaries) {
    boundaries.forEach(boundary => {
      if (boundary.geoJson.geometry.type === 'Polygon') {
        boundary.geoJson.geometry.coordinates[0].forEach(coord => {
          coords.push([coord[0], coord[1]])
        })
      } else if (boundary.geoJson.geometry.type === 'MultiPolygon') {
        boundary.geoJson.geometry.coordinates.forEach(polygon => {
          polygon[0].forEach(coord => {
            coords.push([coord[0], coord[1]])
          })
        })
      }
    })
  }

  if (contacts) {
    contacts.forEach(contact => {
      coords.push([contact.longitude, contact.latitude])
    })
  }

  if (households) {
    households.forEach(household => {
      coords.push([household.longitude, household.latitude])
    })
  }

  if (workplaces) {
    workplaces.forEach(wp => {
      coords.push([wp.longitude, wp.latitude])
    })
  }

  if (route?.geometry) {
    route.geometry.coordinates.forEach(coord => {
      coords.push([coord[0], coord[1]])
    })
  }

  if (coords.length === 0) return null

  const lngs = coords.map(c => c[0])
  const lats = coords.map(c => c[1])
  const minLng = Math.min(...lngs)
  const maxLng = Math.max(...lngs)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}
